import { Emulator } from "../../src";
import { Audio } from "../audio";
import { MobileScreen } from "./mobile-screen";
import { TouchController } from "./touch-controller";
import { CheatManager } from "./cheat-manager";

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

type BuiltinRom = {
  label: string;
  path: string;
  storageKey: string;
};

const BUILTIN_ROMS: BuiltinRom[] = [
  {
    label: "坦克大战 (测试)",
    path: "rom/坦克大战.nes",
    storageKey: "tank-battle",
  },
  {
    label: "重装机兵 (CN 测试)",
    path: "rom/重装机兵CN.nes",
    storageKey: "metal-max-cn",
  },
];

const screenEl = document.getElementById("screen") as HTMLCanvasElement;
const overlayEl = document.getElementById("boot-overlay") as HTMLDivElement;
const bootBtn = document.getElementById("boot-play") as HTMLButtonElement;
const romSelect = document.getElementById("rom-select") as HTMLSelectElement;
const uploadInput = document.getElementById("rom-upload") as HTMLInputElement;
const statusText = document.getElementById("status-text") as HTMLDivElement;
const saveBtn = document.getElementById("save-btn") as HTMLButtonElement;
const loadBtn = document.getElementById("load-btn") as HTMLButtonElement;
const saveStatus = document.getElementById("save-status") as HTMLDivElement;
const saveSlotsContainer = document.getElementById(
  "save-slots"
) as HTMLDivElement;
const saveUploadInput = document.getElementById(
  "save-upload"
) as HTMLInputElement;
const installBtn = document.getElementById("install-btn") as HTMLButtonElement;
const menuToggle = document.getElementById("menu-toggle") as HTMLButtonElement;
const menuPanel = document.getElementById("menu-panel") as HTMLElement;
const menuOverlay = document.getElementById("menu-overlay") as HTMLDivElement;
const cheatValue = document.getElementById("cheat-value") as HTMLInputElement;
const cheatDataType = document.getElementById(
  "cheat-data-type"
) as HTMLSelectElement;
const cheatEndianness = document.getElementById(
  "cheat-endianness"
) as HTMLSelectElement;
const cheatSearch = document.getElementById(
  "cheat-search"
) as HTMLButtonElement;
const cheatReset = document.getElementById("cheat-reset") as HTMLButtonElement;
const cheatChanged = document.getElementById(
  "cheat-changed"
) as HTMLButtonElement;
const cheatIncreased = document.getElementById(
  "cheat-increased"
) as HTMLButtonElement;
const cheatDecreased = document.getElementById(
  "cheat-decreased"
) as HTMLButtonElement;
const cheatStatus = document.getElementById("cheat-status") as HTMLDivElement;
const cheatResults = document.getElementById("cheat-results") as HTMLDivElement;
const cheatSavedSection = document.getElementById(
  "cheat-saved-section"
) as HTMLDivElement;
const cheatSavedList = document.getElementById(
  "cheat-saved-list"
) as HTMLDivElement;
const cheatClearSaved = document.getElementById(
  "cheat-clear-saved"
) as HTMLButtonElement;
const cheatLockedSection = document.getElementById(
  "cheat-locked-section"
) as HTMLDivElement;
const cheatLockedList = document.getElementById(
  "cheat-locked-list"
) as HTMLDivElement;
const cheatClearLocked = document.getElementById(
  "cheat-clear-locked"
) as HTMLButtonElement;

const audio = new Audio();
const screen = new MobileScreen(screenEl);
const cheatManager = new CheatManager();

let lockUpdateInterval: number | null = null;

let audioInitialized = false;
let emulator: Emulator | null = null;
let touchController: TouchController | null = null;
let rafHandle: number | null = null;
let currentRomKey = "";
let deferredPrompt: InstallPromptEvent | null = null;
let selectedSlotId: string | null = null;

type SaveSlotRecord = {
  id: string;
  timestamp: number;
  screenshot: string | null;
  state: any;
};

type SaveStore = {
  version: 1;
  slots: SaveSlotRecord[];
};

function populateRomPicker(): void {
  BUILTIN_ROMS.forEach((rom) => {
    const option = document.createElement("option");
    option.value = rom.path;
    option.dataset.storageKey = rom.storageKey;
    option.textContent = rom.label;
    romSelect.appendChild(option);
  });
}

async function ensureAudioUnlocked(): Promise<void> {
  if (!audioInitialized) {
    audio.start();
    audioInitialized = true;
  }

  try {
    await audio.resume();
  } catch (err) {
    console.warn("Audio resume failed", err);
  }
}

function getSaveKey(storageKey: string): string {
  return `tsnes:sram:${storageKey}`;
}

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  try {
    return new Intl.DateTimeFormat("zh-CN", {
      hour12: false,
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  } catch (error) {
    console.warn("Failed to format timestamp", error);
    return date.toLocaleString();
  }
}

function parseSaveStore(raw: string): SaveStore | null {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return {
        version: 1,
        slots: [],
      };
    }

    if (parsed && typeof parsed === "object" && parsed.version === 1) {
      const slots = Array.isArray(parsed.slots) ? parsed.slots : [];
      const normalized = slots
        .map((slot: SaveSlotRecord) => {
          if (!slot || typeof slot !== "object") {
            return null;
          }

          if (!slot.id || !slot.state) {
            return null;
          }

          return {
            id: String(slot.id),
            timestamp: Number(slot.timestamp) || Date.now(),
            screenshot:
              typeof slot.screenshot === "string" ? slot.screenshot : null,
            state: slot.state,
          } as SaveSlotRecord;
        })
        .filter((slot): slot is SaveSlotRecord => slot !== null);

      return {
        version: 1,
        slots: normalized,
      };
    }
  } catch (error) {
    console.warn("Failed to parse save store", error);
  }

  return null;
}

function loadSaveStore(storageKey: string): SaveStore | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(getSaveKey(storageKey));
  } catch (error) {
    console.warn("Failed to read save store", error);
    return null;
  }

  if (!raw) {
    return null;
  }

  return parseSaveStore(raw);
}

function writeSaveStore(storageKey: string, store: SaveStore): void {
  localStorage.setItem(getSaveKey(storageKey), JSON.stringify(store));
}

function captureScreenshot(): string | null {
  try {
    return screenEl.toDataURL("image/png");
  } catch (error) {
    console.warn("Failed to capture screenshot", error);
    return null;
  }
}

function getSlotsSorted(store: SaveStore | null): SaveSlotRecord[] {
  if (!store) {
    return [];
  }

  return [...store.slots].sort((a, b) => b.timestamp - a.timestamp);
}

function getLatestState(storageKey: string): any | null {
  const store = loadSaveStore(storageKey);
  const slot = getSlotsSorted(store)[0];
  if (!slot) {
    return null;
  }

  return slot.state;
}

function updateSelectedSlot(slots: SaveSlotRecord[]): void {
  if (!slots.length) {
    selectedSlotId = null;
    return;
  }

  if (!selectedSlotId || !slots.some((slot) => slot.id === selectedSlotId)) {
    selectedSlotId = slots[0].id;
  }
}

function renderSaveSlots(storageKey: string): void {
  if (!saveStatus || !saveSlotsContainer) {
    return;
  }

  const slots = getSlotsSorted(loadSaveStore(storageKey));
  updateSelectedSlot(slots);

  saveSlotsContainer.innerHTML = "";

  if (!slots.length) {
    saveStatus.textContent = "暂无存档";
    loadBtn.disabled = true;
    return;
  }

  saveStatus.textContent = `最近保存：${formatTimestamp(slots[0].timestamp)}`;
  loadBtn.disabled = false;

  slots.forEach((slot) => {
    const item = document.createElement("div");
    item.className = "save-slot";
    item.dataset.slotId = slot.id;

    if (slot.id === selectedSlotId) {
      item.classList.add("selected");
    }

    const preview = document.createElement("div");
    preview.className = "save-thumbnail";

    if (slot.screenshot) {
      const image = document.createElement("img");
      image.src = slot.screenshot;
      image.alt = "存档截图";
      preview.appendChild(image);
    } else {
      preview.textContent = "无截图";
    }

    const meta = document.createElement("div");
    meta.className = "save-meta";

    const title = document.createElement("div");
    title.className = "save-title";
    title.textContent = "手动存档";

    const time = document.createElement("div");
    time.className = "save-time";
    time.textContent = formatTimestamp(slot.timestamp);

    meta.appendChild(title);
    meta.appendChild(time);

    const actions = document.createElement("div");
    actions.className = "slot-actions";

    const downloadBtn = document.createElement("button");
    downloadBtn.className = "download-slot";
    downloadBtn.type = "button";
    downloadBtn.textContent = "下载";
    downloadBtn.setAttribute("aria-label", "下载存档");

    const deleteBtn = document.createElement("button");
    deleteBtn.className = "delete-slot";
    deleteBtn.type = "button";
    deleteBtn.textContent = "删除";
    deleteBtn.setAttribute("aria-label", "删除存档");

    actions.appendChild(downloadBtn);
    actions.appendChild(deleteBtn);

    item.appendChild(preview);
    item.appendChild(meta);
    item.appendChild(actions);

    saveSlotsContainer.appendChild(item);
  });
}

function persistState(storageKey: string): void {
  if (!emulator) {
    return;
  }

  const store = loadSaveStore(storageKey) ?? { version: 1, slots: [] };
  const screenshot = captureScreenshot();
  const now = Date.now();
  const state = emulator.saveState();

  const newSlot: SaveSlotRecord = {
    id: `manual-${now}`,
    timestamp: now,
    screenshot,
    state,
  };

  const updatedSlots = [...store.slots, newSlot];
  selectedSlotId = newSlot.id;

  updatedSlots.sort((a, b) => b.timestamp - a.timestamp);

  // Keep only the latest 5 manual saves
  store.slots = updatedSlots.slice(0, 5);

  try {
    writeSaveStore(storageKey, store);
    renderSaveSlots(storageKey);
    setStatus("存档已保存");
  } catch (error) {
    console.error("Failed to persist state", error);
    setStatus("保存失败：存储空间不足");
  }
}

function restoreState(storageKey: string, slotId?: string): void {
  if (!emulator) {
    return;
  }

  const slots = getSlotsSorted(loadSaveStore(storageKey));
  if (!slots.length) {
    setStatus("没有可用的存档");
    return;
  }

  const target = slots.find((slot) => slot.id === slotId) ?? slots[0];

  try {
    emulator.loadState(target.state);
    selectedSlotId = target.id;
    renderSaveSlots(storageKey);
    setStatus("已载入存档");
  } catch (error) {
    console.error("Failed to restore state", error);
    setStatus("读取存档失败");
  }
}

function deleteSaveSlot(storageKey: string, slotId: string): void {
  const store = loadSaveStore(storageKey);
  if (!store) {
    return;
  }

  const nextSlots = store.slots.filter((slot) => slot.id !== slotId);
  if (nextSlots.length === store.slots.length) {
    return;
  }

  try {
    if (!nextSlots.length) {
      localStorage.removeItem(getSaveKey(storageKey));
      selectedSlotId = null;
    } else {
      if (selectedSlotId === slotId) {
        selectedSlotId = nextSlots[0].id;
      }
      writeSaveStore(storageKey, { version: 1, slots: nextSlots });
    }

    renderSaveSlots(storageKey);
    setStatus("存档已删除");
  } catch (error) {
    console.error("Failed to delete save", error);
    setStatus("删除存档失败");
  }
}

function downloadSaveSlot(storageKey: string, slotId: string): void {
  const store = loadSaveStore(storageKey);
  if (!store) {
    return;
  }

  const slot = store.slots.find((s) => s.id === slotId);
  if (!slot) {
    return;
  }

  try {
    const saveData = {
      version: 1,
      storageKey,
      slot,
      exportDate: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(saveData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `tsnes-save-${storageKey}-${slotId}.sav`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setStatus("存档已下载");
  } catch (error) {
    console.error("Failed to download save", error);
    setStatus("下载存档失败");
  }
}

function uploadSaveFile(file: File): void {
  const reader = new FileReader();

  reader.onload = (event) => {
    try {
      const content = event.target?.result as string;
      const saveData = JSON.parse(content);

      if (
        !saveData ||
        saveData.version !== 1 ||
        !saveData.slot ||
        !saveData.storageKey
      ) {
        setStatus("无效的存档文件格式");
        return;
      }

      const storageKey = saveData.storageKey;
      const slot = saveData.slot as SaveSlotRecord;

      // Generate a new ID to avoid conflicts
      const newId = `manual-${Date.now()}`;
      slot.id = newId;
      slot.timestamp = Date.now();

      const store = loadSaveStore(storageKey) ?? { version: 1, slots: [] };
      store.slots.push(slot);

      // Keep only the latest 5 saves
      store.slots.sort((a, b) => b.timestamp - a.timestamp);
      store.slots = store.slots.slice(0, 5);

      writeSaveStore(storageKey, store);

      // If this ROM is currently loaded, update the UI
      if (currentRomKey === storageKey) {
        selectedSlotId = newId;
        renderSaveSlots(storageKey);
      }

      setStatus("存档已导入");
    } catch (error) {
      console.error("Failed to upload save", error);
      setStatus("上传存档失败：文件格式错误");
    }
  };

  reader.onerror = () => {
    setStatus("读取文件失败");
  };

  reader.readAsText(file);
}

function cleanupCurrentGame(): void {
  if (rafHandle !== null) {
    cancelAnimationFrame(rafHandle);
    rafHandle = null;
  }

  if (lockUpdateInterval !== null) {
    clearInterval(lockUpdateInterval);
    lockUpdateInterval = null;
  }

  touchController?.destroy();
  touchController = null;
  emulator = null;
  cheatManager.setEmulator(null);
  currentRomKey = "";
  saveBtn.disabled = true;
  loadBtn.disabled = true;
  if (saveSlotsContainer) {
    saveSlotsContainer.innerHTML = "";
  }
  if (saveStatus) {
    saveStatus.textContent = "暂无存档";
  }
  selectedSlotId = null;
  updateCheatUI();
}

function setStatus(message: string): void {
  statusText.textContent = message;
}

function updateCheatUI(): void {
  const hasEmulator = emulator !== null;
  const isSearchActive = cheatManager.isSearchActive();
  const matchCount = cheatManager.getMatchCount();

  cheatValue.disabled = !hasEmulator;
  cheatDataType.disabled = !hasEmulator;
  cheatEndianness.disabled = !hasEmulator;
  cheatSearch.disabled = !hasEmulator;
  cheatReset.disabled = !hasEmulator || !isSearchActive;
  cheatChanged.disabled = !hasEmulator || !isSearchActive;
  cheatIncreased.disabled = !hasEmulator || !isSearchActive;
  cheatDecreased.disabled = !hasEmulator || !isSearchActive;

  updateCheatValueInputRange();

  if (!hasEmulator) {
    cheatStatus.textContent = "未激活搜索";
    cheatResults.innerHTML = "";
  } else if (!isSearchActive) {
    cheatStatus.textContent = "输入数值开始搜索";
    cheatResults.innerHTML = "";
  } else {
    cheatStatus.textContent = `找到 ${matchCount} 个匹配`;
    renderCheatResults();
  }

  renderSavedAddresses();
  renderLockedAddresses();
}

function updateCheatValueInputRange(): void {
  const maxValue = cheatManager.getMaxValue();
  cheatValue.max = maxValue.toString();

  const dataType = cheatManager.getDataType();
  if (dataType === "uint8") {
    cheatValue.placeholder = "输入数值 (0-255)";
  } else if (dataType === "uint16") {
    cheatValue.placeholder = "输入数值 (0-65535)";
  } else {
    cheatValue.placeholder = "输入数值 (0-4294967295)";
  }
}

function renderCheatResults(): void {
  const matches = cheatManager.getMatches();
  const maxValue = cheatManager.getMaxValue();
  cheatResults.innerHTML = "";

  const maxDisplay = 20;
  const displayMatches = matches.slice(0, maxDisplay);

  displayMatches.forEach((match) => {
    const item = document.createElement("div");
    item.className = "cheat-result-item";

    const address = document.createElement("span");
    address.className = "cheat-address";
    address.textContent = `0x${match.address.toString(16).toUpperCase().padStart(4, "0")}`;

    const value = document.createElement("span");
    value.className = "cheat-value-display";
    value.textContent = `= ${match.value}`;

    const modify = document.createElement("div");
    modify.className = "cheat-modify";

    const input = document.createElement("input");
    input.type = "number";
    input.min = "0";
    input.max = maxValue.toString();
    input.value = match.value.toString();
    input.dataset.address = match.address.toString();

    const modifyBtn = document.createElement("button");
    modifyBtn.textContent = "改";
    modifyBtn.type = "button";
    modifyBtn.dataset.address = match.address.toString();
    modifyBtn.dataset.action = "modify";

    const lockBtn = document.createElement("button");
    lockBtn.textContent = cheatManager.isAddressLocked(match.address)
      ? "🔒"
      : "🔓";
    lockBtn.type = "button";
    lockBtn.title = cheatManager.isAddressLocked(match.address)
      ? "解锁"
      : "锁定";
    lockBtn.dataset.address = match.address.toString();
    lockBtn.dataset.action = "lock";

    const saveBtn = document.createElement("button");
    saveBtn.textContent = "★";
    saveBtn.type = "button";
    saveBtn.title = "收藏";
    saveBtn.dataset.address = match.address.toString();
    saveBtn.dataset.action = "save";

    modify.appendChild(input);
    modify.appendChild(modifyBtn);
    modify.appendChild(lockBtn);
    modify.appendChild(saveBtn);

    item.appendChild(address);
    item.appendChild(value);
    item.appendChild(modify);

    cheatResults.appendChild(item);
  });

  if (matches.length > maxDisplay) {
    const more = document.createElement("div");
    more.style.cssText =
      "text-align: center; padding: 8px; font-size: 11px; opacity: 0.7;";
    more.textContent = `... 还有 ${matches.length - maxDisplay} 个结果（继续搜索以缩小范围）`;
    cheatResults.appendChild(more);
  }
}

function renderSavedAddresses(): void {
  const saved = cheatManager.getSavedAddresses();
  cheatSavedList.innerHTML = "";

  if (saved.length === 0) {
    cheatSavedSection.style.display = "none";
    return;
  }

  cheatSavedSection.style.display = "block";
  cheatClearSaved.disabled = false;

  saved.forEach((addr) => {
    const item = document.createElement("div");
    item.className = "cheat-saved-item";
    if (cheatManager.isAddressLocked(addr.address)) {
      item.classList.add("locked");
    }

    const name = document.createElement("span");
    name.className = "cheat-item-name";
    name.textContent = addr.name;

    const address = document.createElement("span");
    address.className = "cheat-item-address";
    address.textContent = `0x${addr.address.toString(16).toUpperCase().padStart(4, "0")}`;

    const value = document.createElement("span");
    value.className = "cheat-item-value";
    value.textContent = `=${addr.value}`;

    const actions = document.createElement("div");
    actions.className = "cheat-item-actions";

    const loadBtn = document.createElement("button");
    loadBtn.className = "cheat-item-btn";
    loadBtn.textContent = "载入";
    loadBtn.dataset.id = addr.id;
    loadBtn.dataset.action = "load";

    const deleteBtn = document.createElement("button");
    deleteBtn.className = "cheat-item-btn";
    deleteBtn.textContent = "删";
    deleteBtn.dataset.id = addr.id;
    deleteBtn.dataset.action = "delete-saved";

    actions.appendChild(loadBtn);
    actions.appendChild(deleteBtn);

    item.appendChild(name);
    item.appendChild(address);
    item.appendChild(value);
    item.appendChild(actions);

    cheatSavedList.appendChild(item);
  });
}

function renderLockedAddresses(): void {
  const locked = cheatManager.getLockedAddresses();
  cheatLockedList.innerHTML = "";

  if (locked.length === 0) {
    cheatLockedSection.style.display = "none";
    return;
  }

  cheatLockedSection.style.display = "block";
  cheatClearLocked.disabled = false;

  locked.forEach((addr) => {
    const item = document.createElement("div");
    item.className = "cheat-locked-item";

    const icon = document.createElement("span");
    icon.className = "cheat-lock-icon";
    icon.textContent = "🔒";

    const address = document.createElement("span");
    address.className = "cheat-item-address";
    address.textContent = `0x${addr.address.toString(16).toUpperCase().padStart(4, "0")}`;

    const value = document.createElement("span");
    value.className = "cheat-item-value";
    value.textContent = `=${addr.value}`;

    const actions = document.createElement("div");
    actions.className = "cheat-item-actions";

    const unlockBtn = document.createElement("button");
    unlockBtn.className = "cheat-item-btn unlock";
    unlockBtn.textContent = "解锁";
    unlockBtn.dataset.address = addr.address.toString();
    unlockBtn.dataset.action = "unlock";

    actions.appendChild(unlockBtn);

    item.appendChild(icon);
    item.appendChild(address);
    item.appendChild(value);
    item.appendChild(actions);

    cheatLockedList.appendChild(item);
  });
}

function performCheatSearch(): void {
  const value = parseInt(cheatValue.value, 10);
  const maxValue = cheatManager.getMaxValue();

  if (isNaN(value) || value < 0 || value > maxValue) {
    setStatus(`请输入有效数值（0-${maxValue}）`);
    return;
  }

  try {
    cheatManager.searchValue(value);
    updateCheatUI();
    const count = cheatManager.getMatchCount();
    if (count === 0) {
      setStatus("未找到匹配，请重置后重试");
    } else if (count > 100) {
      setStatus(`找到 ${count} 个匹配，继续搜索缩小范围`);
    } else {
      setStatus(`找到 ${count} 个匹配`);
    }
  } catch (error) {
    console.error("Search failed", error);
    setStatus("搜索失败");
  }
}

function performCheatChanged(): void {
  try {
    cheatManager.searchChanged();
    updateCheatUI();
    const count = cheatManager.getMatchCount();
    if (count === 0) {
      setStatus("没有值发生变化");
    } else {
      setStatus(`${count} 个值已变化`);
    }
  } catch (error) {
    console.error("Search changed failed", error);
    setStatus("搜索失败");
  }
}

function performCheatIncreased(): void {
  try {
    cheatManager.searchIncreased();
    updateCheatUI();
    const count = cheatManager.getMatchCount();
    if (count === 0) {
      setStatus("没有值增加");
    } else {
      setStatus(`${count} 个值已增加`);
    }
  } catch (error) {
    console.error("Search increased failed", error);
    setStatus("搜索失败");
  }
}

function performCheatDecreased(): void {
  try {
    cheatManager.searchDecreased();
    updateCheatUI();
    const count = cheatManager.getMatchCount();
    if (count === 0) {
      setStatus("没有值减少");
    } else {
      setStatus(`${count} 个值已减少`);
    }
  } catch (error) {
    console.error("Search decreased failed", error);
    setStatus("搜索失败");
  }
}

function resetCheatSearch(): void {
  cheatManager.reset();
  cheatValue.value = "";
  updateCheatUI();
  setStatus("已重置金手指搜索");
}

function startAnimationLoop(instance: Emulator): void {
  const frame = () => {
    instance.frame();
    rafHandle = requestAnimationFrame(frame);
  };
  frame();
}

async function loadBuiltinRom(option: HTMLOptionElement): Promise<void> {
  const storageKey = option.dataset.storageKey ?? option.value;
  setStatus(`加载 ${option.textContent ?? option.value} ...`);

  try {
    const response = await fetch(option.value);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const buffer = new Uint8Array(await response.arrayBuffer());
    await bootEmulator(buffer, option.textContent ?? option.value, storageKey);
  } catch (error) {
    console.error("Failed to load ROM", error);
    setStatus("加载内置 ROM 失败，请检查网络或重新尝试");
  }
}

async function bootEmulator(
  romData: Uint8Array,
  label: string,
  storageKey: string
): Promise<void> {
  cleanupCurrentGame();
  await ensureAudioUnlocked();

  const savedState = getLatestState(storageKey);
  const sramLoad = savedState?.sram
    ? Uint8Array.from(savedState.sram)
    : undefined;

  emulator = new Emulator(romData, {
    sampleRate: audio.sampleRate,
    onSample: (value) => audio.onSample(value),
    onFrame: (frame) => screen.onFrame(frame),
    sramLoad,
  });

  audio.emulator = emulator;
  touchController = new TouchController(emulator, document.body);
  cheatManager.setEmulator(emulator);
  currentRomKey = storageKey;

  overlayEl.classList.add("hidden");
  setStatus(`正在运行：${label}`);
  saveBtn.disabled = false;
  renderSaveSlots(storageKey);
  updateCheatUI();

  startAnimationLoop(emulator);
  startLockUpdateLoop();
}

function startLockUpdateLoop(): void {
  if (lockUpdateInterval !== null) {
    clearInterval(lockUpdateInterval);
  }

  lockUpdateInterval = window.setInterval(() => {
    cheatManager.applyLockedValues();
  }, 16);
}

function toggleMenu(open?: boolean): void {
  const shouldOpen = open ?? !menuPanel.classList.contains("open");

  if (shouldOpen) {
    menuPanel.classList.add("open");
    menuOverlay.classList.add("visible");
    menuToggle.classList.add("active");
  } else {
    menuPanel.classList.remove("open");
    menuOverlay.classList.remove("visible");
    menuToggle.classList.remove("active");
  }
}

function setupEventListeners(): void {
  menuToggle.addEventListener("click", () => {
    toggleMenu();
  });

  menuOverlay.addEventListener("click", () => {
    toggleMenu(false);
  });

  bootBtn.addEventListener("click", async () => {
    await ensureAudioUnlocked();
    overlayEl.classList.add("hidden");
    setStatus("请选择 ROM 启动");
  });

  romSelect.addEventListener("change", async () => {
    const option = romSelect.selectedOptions[0];
    if (!option || !option.value) {
      return;
    }

    toggleMenu(false);
    await loadBuiltinRom(option);
  });

  uploadInput.addEventListener("change", async () => {
    const file = uploadInput.files?.[0];
    if (!file) {
      return;
    }

    toggleMenu(false);
    try {
      const arrayBuffer = await new Response(file).arrayBuffer();
      const buffer = new Uint8Array(arrayBuffer);
      await bootEmulator(buffer, file.name, file.name);
      romSelect.value = "";
    } catch (error) {
      console.error("Failed to load uploaded ROM", error);
      setStatus("读取文件失败，请重试");
    } finally {
      uploadInput.value = "";
    }
  });

  saveBtn.addEventListener("click", () => {
    if (!currentRomKey) {
      return;
    }
    persistState(currentRomKey);
    toggleMenu(false);
  });

  loadBtn.addEventListener("click", () => {
    if (!currentRomKey) {
      return;
    }
    restoreState(currentRomKey, selectedSlotId ?? undefined);
    toggleMenu(false);
  });

  saveSlotsContainer.addEventListener("click", (event) => {
    if (!currentRomKey) {
      return;
    }

    const target = event.target as HTMLElement;
    const slotEl = target.closest<HTMLDivElement>(".save-slot");
    if (!slotEl) {
      return;
    }

    const slotId = slotEl.dataset.slotId;
    if (!slotId) {
      return;
    }

    if (target.closest(".download-slot")) {
      downloadSaveSlot(currentRomKey, slotId);
      event.stopPropagation();
      return;
    }

    if (target.closest(".delete-slot")) {
      deleteSaveSlot(currentRomKey, slotId);
      event.stopPropagation();
      return;
    }

    selectedSlotId = slotId;
    renderSaveSlots(currentRomKey);
  });

  saveUploadInput.addEventListener("change", () => {
    const file = saveUploadInput.files?.[0];
    if (!file) {
      return;
    }

    uploadSaveFile(file);
    saveUploadInput.value = "";
  });

  cheatDataType.addEventListener("change", () => {
    const dataType = cheatDataType.value as any;
    cheatManager.setDataType(dataType);
    updateCheatUI();
  });

  cheatEndianness.addEventListener("change", () => {
    const endianness = cheatEndianness.value as any;
    cheatManager.setEndianness(endianness);
    updateCheatUI();
  });

  cheatSearch.addEventListener("click", () => {
    performCheatSearch();
  });

  cheatValue.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      performCheatSearch();
    }
  });

  cheatReset.addEventListener("click", () => {
    resetCheatSearch();
  });

  cheatChanged.addEventListener("click", () => {
    performCheatChanged();
  });

  cheatIncreased.addEventListener("click", () => {
    performCheatIncreased();
  });

  cheatDecreased.addEventListener("click", () => {
    performCheatDecreased();
  });

  cheatResults.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.tagName !== "BUTTON" || !target.dataset.address) {
      return;
    }

    const address = parseInt(target.dataset.address, 10);
    const action = target.dataset.action;

    if (action === "lock") {
      if (cheatManager.isAddressLocked(address)) {
        cheatManager.unlockAddress(address);
        setStatus(`已解锁 0x${address.toString(16).toUpperCase()}`);
      } else {
        // 锁定时使用输入框中的值
        const inputEl = target.parentElement?.querySelector(
          "input"
        ) as HTMLInputElement;
        const value = inputEl
          ? parseInt(inputEl.value, 10)
          : cheatManager.readValue(address);

        if (isNaN(value)) {
          setStatus("请输入有效数值");
          return;
        }

        // 先写入值，再锁定
        cheatManager.writeValue(address, value);
        cheatManager.lockAddress(address, value);
        setStatus(`已锁定 0x${address.toString(16).toUpperCase()} = ${value}`);
      }
      updateCheatUI();
    } else if (action === "save") {
      const name = prompt(
        "请输入地址名称:",
        `地址_${address.toString(16).toUpperCase()}`
      );
      if (name) {
        cheatManager.saveAddress(address, name);
        setStatus(`已收藏 ${name}`);
        updateCheatUI();
      }
    } else if (action === "modify") {
      const inputEl = target.parentElement?.querySelector("input");
      if (!inputEl) {
        return;
      }

      const newValue = parseInt(inputEl.value, 10);
      const maxValue = cheatManager.getMaxValue();

      if (isNaN(newValue) || newValue < 0 || newValue > maxValue) {
        setStatus(`请输入有效数值（0-${maxValue}）`);
        return;
      }

      try {
        cheatManager.writeValue(address, newValue);

        // 如果地址已经被锁定，更新锁定的值
        if (cheatManager.isAddressLocked(address)) {
          cheatManager.lockAddress(address, newValue);
          setStatus(
            `已修改并锁定 0x${address.toString(16).toUpperCase()} = ${newValue}`
          );
        } else {
          setStatus(
            `已修改 0x${address.toString(16).toUpperCase()} = ${newValue}`
          );
        }

        setTimeout(() => {
          updateCheatUI();
        }, 100);
      } catch (error) {
        console.error("Failed to write value", error);
        setStatus("修改失败");
      }
    }
  });

  cheatSavedList.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.tagName !== "BUTTON") {
      return;
    }

    const action = target.dataset.action;
    const id = target.dataset.id;

    if (action === "load" && id) {
      const saved = cheatManager.getSavedAddresses().find((s) => s.id === id);
      if (saved) {
        cheatManager.loadSavedAddress(saved);

        // 更新 UI 的数据类型和字节序选择器
        cheatDataType.value = saved.dataType;
        cheatEndianness.value = saved.endianness;

        // 清空搜索框
        cheatValue.value = "";

        setStatus(
          `已载入 ${saved.name} (0x${saved.address.toString(16).toUpperCase()})`
        );
        updateCheatUI();
      }
    } else if (action === "delete-saved" && id) {
      cheatManager.removeSavedAddress(id);
      setStatus("已删除收藏");
      updateCheatUI();
    }
  });

  cheatLockedList.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.tagName !== "BUTTON") {
      return;
    }

    const action = target.dataset.action;
    const address = target.dataset.address;

    if (action === "unlock" && address) {
      const addr = parseInt(address, 10);
      cheatManager.unlockAddress(addr);
      setStatus(`已解锁 0x${addr.toString(16).toUpperCase()}`);
      updateCheatUI();
    }
  });

  cheatClearSaved.addEventListener("click", () => {
    if (confirm("确定要清空所有收藏地址吗？")) {
      cheatManager.clearSavedAddresses();
      setStatus("已清空收藏");
      updateCheatUI();
    }
  });

  cheatClearLocked.addEventListener("click", () => {
    if (confirm("确定要解锁所有地址吗？")) {
      cheatManager.clearAllLocks();
      setStatus("已全部解锁");
      updateCheatUI();
    }
  });

  window.addEventListener("pointerdown", () => ensureAudioUnlocked(), {
    once: true,
  });
  window.addEventListener("touchstart", () => ensureAudioUnlocked(), {
    once: true,
  });

  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("service-worker.js").catch((err) => {
        console.warn("Service worker registration failed", err);
      });
    });
  }

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    installBtn.classList.add("visible");
  });

  installBtn.addEventListener("click", async () => {
    if (!deferredPrompt) {
      return;
    }

    installBtn.disabled = true;
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } finally {
      deferredPrompt = null;
      installBtn.disabled = false;
      installBtn.classList.remove("visible");
    }
  });

  window.addEventListener("pagehide", () => {
    if (audioInitialized) {
      audio.suspend().catch(() => undefined);
    }
  });
}

function init(): void {
  populateRomPicker();
  setupEventListeners();
  setStatus("未加载 ROM");
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
